import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Save, BarChart3, Plus, RefreshCw, PieChart, X } from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { QBANK_BOARDS, QBANK_GRADES_MAP } from './QuestionBankAdmin';

export const ChapterWeightageAdmin = () => {
  const [board, setBoard] = useState('CBSE');
  const [grade, setGrade] = useState('10');
  const [subject, setSubject] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSubjects(); }, [board, grade]);
  useEffect(() => { if (subject) fetchWeightage(); }, [board, grade, subject]);

  const fetchSubjects = async () => {
    try {
      const data = await (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/board-exams/subjects?board=${board}&grade=${grade}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })).json();
      setSubjects(data.subjects || []);
      setSubject('');
      setChapters([]);
    } catch { setSubjects([]); }
  };

  const fetchWeightage = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getChapterWeightage(board, parseInt(grade), subject);
      setChapters(data.chapters || []);
      setSource(data.source || 'none');
    } catch { setChapters([]); setSource('none'); }
    finally { setLoading(false); }
  };

  const updateWeight = (idx, value) => {
    setChapters(prev => prev.map((c, i) => i === idx ? { ...c, weightage: parseInt(value) || 0 } : c));
  };

  const addChapter = () => {
    setChapters(prev => [...prev, { chapter: '', weightage: 5 }]);
  };

  const removeChapter = (idx) => {
    setChapters(prev => prev.filter((_, i) => i !== idx));
  };

  const updateChapterName = (idx, name) => {
    setChapters(prev => prev.map((c, i) => i === idx ? { ...c, chapter: name } : c));
  };

  const handleSave = async () => {
    if (!subject) return;
    setSaving(true);
    try {
      await adminApi.updateChapterWeightage(board, parseInt(grade), subject, chapters);
      toast.success('Weightage saved');
      setSource('custom');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const totalWeight = chapters.reduce((sum, c) => sum + (c.weightage || 0), 0);

  return (
    <div className="space-y-6" data-testid="chapter-weightage-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><PieChart className="w-6 h-6" /> Chapter Weightage</h1>
        <p className="text-muted-foreground text-sm mt-1">Define chapter-wise mark distribution for exam generation.</p>
      </div>

      {/* Selectors */}
      <div className="grid grid-cols-3 gap-4">
        <Select value={board} onValueChange={(v) => { setBoard(v); const g = QBANK_GRADES_MAP[v]; if (!g.includes(parseInt(grade))) setGrade(String(g[0])); }}>
          <SelectTrigger data-testid="cw-board"><SelectValue /></SelectTrigger>
          <SelectContent>{QBANK_BOARDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={grade} onValueChange={setGrade}>
          <SelectTrigger data-testid="cw-grade"><SelectValue /></SelectTrigger>
          <SelectContent>{(QBANK_GRADES_MAP[board] || []).map(g => <SelectItem key={g} value={String(g)}>Class {g}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={subject} onValueChange={setSubject}>
          <SelectTrigger data-testid="cw-subject"><SelectValue placeholder="Select Subject" /></SelectTrigger>
          <SelectContent>{subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Chapter Editor */}
      {subject && (
        <div className="rounded-xl border bg-card p-5 space-y-4" data-testid="cw-editor">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">{board} Class {grade} - {subject}</h3>
              <span className="text-xs text-muted-foreground mt-0.5">
                Source: <Badge variant="outline" className="text-xs">{source}</Badge> | Total: <span className={totalWeight === 100 ? 'text-emerald-600 font-bold' : 'text-amber-600 font-bold'}>{totalWeight}%</span>
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={addChapter}><Plus className="w-3 h-3 mr-1" /> Add Chapter</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="cw-save-btn">
                {saving ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
              </Button>
            </div>
          </div>

          {loading ? <div className="skeleton h-32 rounded-lg" /> : chapters.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No chapter weightage defined. Click "Add Chapter" to define the distribution.
            </div>
          ) : (
            <div className="space-y-2">
              {chapters.map((ch, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30" data-testid={`cw-chapter-${idx}`}>
                  <span className="text-xs text-muted-foreground w-6">{idx + 1}.</span>
                  <Input
                    value={ch.chapter}
                    onChange={(e) => updateChapterName(idx, e.target.value)}
                    placeholder="Chapter name"
                    className="flex-1 h-8 text-sm"
                  />
                  <div className="flex items-center gap-1 w-24">
                    <Input
                      type="number"
                      value={ch.weightage}
                      onChange={(e) => updateWeight(idx, e.target.value)}
                      className="h-8 text-sm w-16 text-center"
                      min={0} max={100}
                    />
                    <span className="text-xs text-muted-foreground">%</span>
                  </div>
                  <Progress value={ch.weightage} className="w-20 h-2" />
                  <Button variant="ghost" size="sm" onClick={() => removeChapter(idx)} className="h-8 w-8 p-0 text-red-400 hover:text-red-600">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// Lab Analytics Component - Admin/Teacher
